// src/componentes/Comisiones/ComisionistaDetalle.jsx
// Pantalla de detalle de un comisionista con sus comisiones activas.

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaToggleOff, FaToggleOn } from "react-icons/fa";
import { getComisionista, toggleComisionistaActivo } from "../../services/comisionistasService";
import { listarComisiones } from "../../services/comisionesService";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import notify from "../common/notify";
import { dialogService } from "../common/dialogService";
import "./Comisiones.css";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const formatPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

const ESQUEMA_LABELS = {
  "enganche-100": "100% al cobrar enganche",
  "enganche-50-mensualidades-50": "50% enganche + 50% en mensualidades",
  "escalonado-por-pagos": "30% contrato + 30% enganche + 40% en 6 pagos",
  custom: "Personalizado",
};

const ESTADO_COLOR = {
  activa:             { bg: "#dbeafe", color: "#1d4ed8" },
  totalmenteDevengada:{ bg: "#fef3c7", color: "#b45309" },
  pagada:             { bg: "#dcfce7", color: "#16a34a" },
  cancelada:          { bg: "#fee2e2", color: "#dc2626" },
};

export default function ComisionistaDetalle() {
  const { comisionistaId } = useParams();
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [comisionista, setComisionista] = useState(null);
  const [comisiones, setComisiones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Carga separada: que un fallo en comisiones no oculte al comisionista
      try {
        const c = await getComisionista(comisionistaId);
        if (!alive) return;
        setComisionista(c);
      } catch (err) {
        console.error("getComisionista error:", err);
        notify.error("No se pudo cargar el comisionista.");
      } finally {
        if (alive) setLoading(false);
      }

      try {
        const coms = await listarComisiones({ comisionistaId });
        if (alive) setComisiones(coms);
      } catch (err) {
        console.warn("listarComisiones error (puede ser índice faltante):", err);
        // No bloquea — la página igual muestra los datos del comisionista
      }
    })();
    return () => { alive = false; };
  }, [comisionistaId]);

  const handleToggle = async () => {
    if (!comisionista) return;
    const accion = comisionista.activo ? "desactivar" : "activar";
    const ok = await dialogService.confirm(
      `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} al comisionista ${comisionista.nombre}? Esto actualizará su proveedor espejo.`
    );
    if (!ok) return;
    try {
      await toggleComisionistaActivo(comisionistaId, !comisionista.activo);
      setComisionista((c) => ({ ...c, activo: !c.activo }));
      notify.success(`Comisionista ${comisionista.activo ? "desactivado" : "activado"}.`);
    } catch (err) {
      notify.error("No se pudo actualizar el estado.");
    }
  };

  if (loading) {
    return (
      <div className="com-page">
        <div className="com-panel">
          <p style={{ color: "#94a3b8" }}>Cargando…</p>
        </div>
      </div>
    );
  }

  if (!comisionista) {
    return (
      <div className="com-page">
        <div className="com-panel">
          <p style={{ color: "#94a3b8" }}>Comisionista no encontrado.</p>
          <button className="btn-link" onClick={() => navigate("/app/comisionistas")}>
            ← Volver
          </button>
        </div>
      </div>
    );
  }

  // Separar comisiones vigentes vs canceladas:
  //   - Saldo y Devengado: SOLO vigentes (las canceladas ya no se deben).
  //   - Pagado total: TODAS — refleja el dinero histórico que realmente
  //     salió de caja al comisionista (incluye lo ya pagado en canceladas
  //     que ahora está como recobro pendiente).
  //   - Por recobrar: solo canceladas con pago hecho aún sin perdonar/recuperar.
  const esCancelada = (c) => c?.estado === "cancelada";
  const comisionesVigentes = comisiones.filter((c) => !esCancelada(c));
  const comisionesCanceladas = comisiones.filter(esCancelada);

  const totalSaldo = comisionesVigentes.reduce((acc, c) => acc + Number(c.saldo || 0), 0);
  const totalDevengado = comisionesVigentes.reduce((acc, c) => acc + Number(c.devengado || 0), 0);
  // Pagado real = todas las comisiones (incluye canceladas con pago hecho)
  const totalPagado = comisiones.reduce((acc, c) => acc + Number(c.pagado || 0), 0);

  // Recobro: comisiones canceladas con pago hecho y pendiente de recobrar/perdonar
  const totalRecobroPendiente = comisionesCanceladas.reduce((acc, c) => {
    const rp = c.recobroPendiente;
    if (!rp || rp.estado !== "pendiente") return acc;
    const pend = Math.max(Number(rp.monto || 0) - Number(rp.montoRecuperado || 0), 0);
    return acc + pend;
  }, 0);

  return (
    <div className="com-page">
      <div className="com-panel" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ─── Botón volver ─────────────────────────── */}
        <button
          className="btn-link"
          style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6 }}
          onClick={() => navigate("/app/comisionistas")}
        >
          <FaArrowLeft /> Volver a comisionistas
        </button>

        {/* ─── Header ───────────────────────────────── */}
        <div className="com-head">
          <div>
            <h2>
              {comisionista.nombre} {comisionista.apellidos}
              {comisionista.activo === false && (
                <span className="com-badge-inactivo" style={{ marginLeft: 10 }}>
                  Inactivo
                </span>
              )}
            </h2>
            <p>{comisionista.tipo === "interno" ? "Comisionista interno" : "Comisionista externo"}</p>
          </div>
          {can("comisionistas.gestionar") && (
            <div className="com-head-actions">
              <button
                className="btn btn-secondary"
                onClick={handleToggle}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                {comisionista.activo ? <FaToggleOn /> : <FaToggleOff />}
                {comisionista.activo ? "Desactivar" : "Activar"}
              </button>
              <button
                className="btn btn-primary"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => navigate(`/app/comisionistas/${comisionistaId}/editar`)}
              >
                <FaEdit /> Editar
              </button>
            </div>
          )}
        </div>

        {/* ─── Datos personales ─────────────────────── */}
        <section>
          <h3 style={{ margin: "0 0 10px", fontSize: 15, color: "#334155" }}>Datos de contacto</h3>
          <div className="com-detalle">
            {comisionista.email && (
              <div className="com-detalle-row">
                <span>Email</span>
                <b>{comisionista.email}</b>
              </div>
            )}
            {comisionista.telefono && (
              <div className="com-detalle-row">
                <span>Teléfono</span>
                <b>{comisionista.telefono}</b>
              </div>
            )}
            {comisionista.rfc && (
              <div className="com-detalle-row">
                <span>RFC</span>
                <b style={{ fontFamily: "monospace" }}>{comisionista.rfc}</b>
              </div>
            )}
            {comisionista.banco && (
              <div className="com-detalle-row">
                <span>Banco</span>
                <b>{comisionista.banco}</b>
              </div>
            )}
            {comisionista.cuentaBancaria && (
              <div className="com-detalle-row">
                <span>Cuenta / CLABE</span>
                <b style={{ fontFamily: "monospace" }}>{comisionista.cuentaBancaria}</b>
              </div>
            )}
            <div className="com-detalle-row">
              <span>Tarifa por defecto</span>
              <b>{formatPct(comisionista.tarifaPorDefecto)}</b>
            </div>
            <div className="com-detalle-row">
              <span>Esquema por defecto</span>
              <b style={{ fontSize: 13 }}>
                {ESQUEMA_LABELS[comisionista.esquemaPorDefecto] || comisionista.esquemaPorDefecto}
              </b>
            </div>
            {comisionista.proveedorEspejoId && (
              <div className="com-detalle-row">
                <span>Proveedor espejo</span>
                <button
                  className="btn-link"
                  onClick={() => navigate(`/app/proveedores/${comisionista.proveedorEspejoId}`)}
                >
                  Ver en proveedores →
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ─── Resumen de comisiones ─────────────────── */}
        <section>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, color: "#334155" }}>
            Resumen financiero{" "}
            <span style={{ fontWeight: 400, color: "#64748b", fontSize: 13 }}>
              ({comisionesVigentes.length} vigente{comisionesVigentes.length !== 1 ? "s" : ""}
              {comisionesCanceladas.length > 0 && (
                <> · {comisionesCanceladas.length} cancelada{comisionesCanceladas.length !== 1 ? "s" : ""}</>
              )})
            </span>
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: totalRecobroPendiente > 0 ? "repeat(4, 1fr)" : "repeat(3, 1fr)",
              gap: 10,
              marginBottom: 16,
            }}
          >
            {[
              { label: "Por cobrar (saldo)", value: formatMXN(totalSaldo), color: "#1f4e78" },
              { label: "Devengado total", value: formatMXN(totalDevengado), color: "#b45309" },
              { label: "Pagado total", value: formatMXN(totalPagado), color: "#16a34a" },
              ...(totalRecobroPendiente > 0
                ? [{ label: "Por recobrar", value: formatMXN(totalRecobroPendiente), color: "#dc2626" }]
                : []),
            ].map((kpi) => (
              <div
                key={kpi.label}
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "12px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
                  {kpi.label}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: kpi.color }}>
                  {kpi.value}
                </div>
              </div>
            ))}
          </div>

          {/* Lista de comisiones */}
          {comisiones.length === 0 ? (
            <div className="com-empty">
              Este comisionista aún no tiene comisiones registradas.
            </div>
          ) : (
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Propiedad", "Cliente", "Total", "Pagado", "Saldo", "Estado"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid #e5e7eb",
                        fontWeight: 700,
                        fontSize: 11,
                        color: "#64748b",
                        textTransform: "uppercase",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comisiones.map((com) => {
                  const ec = ESTADO_COLOR[com.estado] || { bg: "#f3f4f6", color: "#6b7280" };
                  return (
                    <tr
                      key={com.id}
                      style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/app/comisiones/${com.id}`, {
                          state: { comisionistaId },
                        })
                      }
                      title="Ver detalle de comisión"
                    >
                      <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                        {com.propiedadFolio || "—"}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#334155" }}>
                        {com.clienteNombre || "—"}
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 700 }}>
                        {formatMXN(com.montoTotal)}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#16a34a", fontWeight: 600 }}>
                        {formatMXN(com.pagado)}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#1f4e78", fontWeight: 700 }}>
                        {formatMXN(com.saldo)}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            background: ec.bg,
                            color: ec.color,
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "3px 9px",
                            borderRadius: 999,
                          }}
                        >
                          {com.estado}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
